import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play } from 'lucide-react';
import { fetchCollection } from '../lib/cms';
import useDocumentMeta from '../hooks/useDocumentMeta';
import Lightbox from '../components/Lightbox';

export default function GalleryPage() {
  useDocumentMeta(
    'Galleri | LIVSLYST ApS',
    'Se billeder og glimt fra hverdagen, de kreative værksteder, haveholdet og de trygge rammer hos LIVSLYST ApS i Brørup.'
  );

  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('Alle');
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    async function loadGallery() {
      const data = await fetchCollection('galleri');
      setGalleryItems(data);
      setLoading(false);
    }
    loadGallery();
  }, []);

  // Extract unique categories
  const categories = ['Alle', ...new Set(galleryItems.map((item) => item.category?.da || item.category))];

  // Filter items based on selected category
  const filteredItems = selectedCategory === 'Alle'
    ? galleryItems
    : galleryItems.filter((item) => (item.category?.da || item.category) === selectedCategory);

  return (
    <div className="bg-stone-50 text-stone-800 antialiased min-h-screen overflow-x-hidden">
      {/* Header Section */}
      <section className="bg-stone-100 py-16 md:py-20 border-b border-stone-200">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4"
        >
          <span className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-bold">
            Visuelle Glimt
          </span>
          <h1 className="text-4xl md:text-5xl font-bold text-stone-900 tracking-tight">
            Vores Galleri
          </h1>
          <p className="text-lg text-stone-600 max-w-2xl mx-auto leading-relaxed">
            Oplev stemningen, aktiviteterne og de trygge, varme rammer hos LIVSLYST ApS.
          </p>
        </motion.div>
      </section>

      {/* Gallery Section */}
      <section className="py-12 md:py-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {loading ? (
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-700"></div>
          </div>
        ) : (
          <div className="space-y-10">
            {/* Category Filter Buttons */}
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="flex flex-wrap justify-center gap-2 md:gap-3"
            >
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => {
                    setSelectedCategory(cat);
                    setLightboxIndex(null); // Reset lightbox index when changing category
                  }}
                  className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer ${
                    selectedCategory === cat
                      ? 'bg-emerald-700 text-white shadow-sm'
                      : 'bg-white text-stone-600 border border-stone-200 hover:bg-stone-100 hover:text-stone-900'
                  }`}
                >
                  {cat}
                </button>
              ))}
            </motion.div>

            {/* Gallery Grid with Layout Animations */}
            <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              <AnimatePresence mode="popLayout">
                {filteredItems.map((item, idx) => (
                  <motion.div
                    key={item.id}
                    layout
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9, transition: { duration: 0.2 } }}
                    transition={{ duration: 0.4 }}
                    onClick={() => setLightboxIndex(idx)}
                    className="group relative rounded-2xl overflow-hidden cursor-pointer border border-stone-200 shadow-sm hover:shadow-md transition-shadow duration-300 h-72 md:h-80"
                  >
                    {item.image?.[0]?.toLowerCase().includes('.mp4') ? (
                      <div className="relative w-full h-full overflow-hidden bg-stone-100">
                        <video
                          src={item.image?.[0]}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          autoPlay
                          muted
                          loop
                          playsInline
                        />
                        <div className="absolute top-4 right-4 bg-stone-900/80 text-white p-2.5 rounded-full backdrop-blur-sm shadow-md z-10">
                          <Play className="w-4 h-4 fill-current text-white" />
                        </div>
                      </div>
                    ) : (
                      <img
                        src={item.image?.[0]}
                        alt={item.title?.da || item.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                    )}
                    {/* Hover Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-stone-950/80 via-stone-950/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6 text-white">
                      <span className="text-xs uppercase tracking-widest text-emerald-400 font-semibold mb-1">
                        {item.category?.da || item.category}
                      </span>
                      <h3 className="text-lg font-bold">
                        {item.title?.da || item.title}
                      </h3>
                      <p className="text-xs text-stone-300 mt-1 line-clamp-2">
                        {item.description?.da || item.description}
                      </p>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>

            {filteredItems.length === 0 && (
              <div className="text-center py-16 text-stone-500">
                Ingen billeder fundet i denne kategori.
              </div>
            )}
          </div>
        )}
      </section>

      {/* Lightbox Integration */}
      {lightboxIndex !== null && filteredItems[lightboxIndex] && (
        <Lightbox
          image={filteredItems[lightboxIndex].image?.[0]}
          title={filteredItems[lightboxIndex].title?.da || filteredItems[lightboxIndex].title}
          description={filteredItems[lightboxIndex].description?.da || filteredItems[lightboxIndex].description}
          category={filteredItems[lightboxIndex].category?.da || filteredItems[lightboxIndex].category}
          onClose={() => setLightboxIndex(null)}
          onPrev={
            lightboxIndex > 0
              ? () => setLightboxIndex(lightboxIndex - 1)
              : null
          }
          onNext={
            lightboxIndex < filteredItems.length - 1
              ? () => setLightboxIndex(lightboxIndex + 1)
              : null
          }
        />
      )}
    </div>
  );
}
